import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { X, Search, Plus } from "lucide-react";

/**
 * Compact fund picker used by both the Overlap and Underperformance tools.
 *
 * Two selection sources:
 *   - db     : any fund from the Admin research DB (searchable)
 *   - held   : a specific client's currently-held funds (only if clientId is passed)
 *   - external: a manual ad-hoc entry (fills in fields inline)
 *
 * `value` is a list of picked entries: { source, id?, name, sub_category?, allocation?, trailing_returns?, amount_invested? }
 */
export default function HeldFundsPicker({ clientId, value, onChange, showAmount = true, minPicks = 1 }) {
  const [dbFunds, setDbFunds] = useState([]);
  const [heldFunds, setHeldFunds] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState(clientId ? "held" : "db");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mutual-funds");
        setDbFunds(data);
      } catch { /* Admin only endpoint — Advisor can't list */ }
      if (clientId) {
        try {
          const { data } = await api.get(`/clients/${clientId}/held-funds`);
          setHeldFunds(data);
        } catch { setHeldFunds([]); }
      }
    })();
  }, [clientId]);

  const pickedIds = useMemo(() => new Set(value.map((v) => `${v.source}:${v.id || v.name}`)), [value]);

  const add = (entry) => {
    const key = `${entry.source}:${entry.id || entry.name}`;
    if (pickedIds.has(key)) return;
    onChange([...value, entry]);
  };
  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };
  const setAmount = (idx, amt) => {
    const next = [...value];
    next[idx] = { ...next[idx], amount_invested: Number(amt) || 0 };
    onChange(next);
  };

  const filteredDb = dbFunds.filter(
    (f) => !q || f.name.toLowerCase().includes(q.toLowerCase()) || (f.sub_category || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-border">
        {clientId && (
          <button
            onClick={() => setTab("held")}
            className={`px-3 py-2 text-sm ${tab === "held" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
            data-testid="picker-tab-held"
          >
            Client&apos;s held funds ({heldFunds.length})
          </button>
        )}
        <button
          onClick={() => setTab("db")}
          className={`px-3 py-2 text-sm ${tab === "db" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
          data-testid="picker-tab-db"
        >
          Research DB ({dbFunds.length})
        </button>
        <button
          onClick={() => setTab("external")}
          className={`px-3 py-2 text-sm ${tab === "external" ? "border-b-2 border-primary text-primary" : "text-muted-foreground"}`}
          data-testid="picker-tab-external"
        >
          Manual entry
        </button>
      </div>

      {tab === "held" && (
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {heldFunds.length === 0 && <p className="text-sm text-muted-foreground">No held funds recorded. Add them from the client&apos;s Held Funds tab.</p>}
          {heldFunds.map((h) => {
            const picked = pickedIds.has(`held:${h.id}`);
            return (
              <button
                key={h.id}
                onClick={() => add({
                  source: "held", id: h.id,
                  name: h.external_fund_name, sub_category: h.external_sub_category,
                  market_cap_allocation: h.market_cap_allocation,
                  sector_allocation: h.sector_allocation,
                  trailing_returns: h.trailing_returns,
                  amount_invested: h.amount_invested,
                })}
                disabled={picked}
                className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${
                  picked ? "border-primary/20 bg-primary/5 opacity-60" : "border-border bg-white hover:bg-secondary/40"
                }`}
                data-testid={`held-fund-${h.id}`}
              >
                <span>
                  <span className="font-medium">{h.external_fund_name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{h.external_sub_category}</span>
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  ₹{Number(h.amount_invested || 0).toLocaleString("en-IN")}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {tab === "db" && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search research DB…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="picker-search-input" />
          </div>
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {filteredDb.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
            {filteredDb.map((f) => {
              const picked = pickedIds.has(`db:${f.id}`);
              return (
                <button
                  key={f.id}
                  onClick={() => add({
                    source: "db", id: f.id, name: f.name, sub_category: f.sub_category,
                    market_cap_allocation: f.market_cap_allocation,
                    sector_allocation: f.sector_allocation,
                    trailing_returns: f.trailing_returns,
                  })}
                  disabled={picked}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm ${
                    picked ? "border-primary/20 bg-primary/5 opacity-60" : "border-border bg-white hover:bg-secondary/40"
                  }`}
                  data-testid={`db-fund-${f.id}`}
                >
                  <span>
                    <span className="font-medium">{f.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{f.sub_category}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {tab === "external" && <ExternalEntry onAdd={add} />}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="overline text-muted-foreground">Selected · {value.length} fund{value.length === 1 ? "" : "s"}</span>
          {value.length < minPicks && <span className="text-xs text-destructive">Select at least {minPicks} fund{minPicks === 1 ? "" : "s"}</span>}
        </div>
        {value.length === 0 && <p className="text-sm text-muted-foreground">No funds selected yet.</p>}
        <div className="space-y-2">
          {value.map((v, idx) => (
            <div key={`${v.source}-${v.id || v.name}-${idx}`} className="flex items-center justify-between rounded-md border border-border bg-white p-3 text-sm" data-testid={`picked-${idx}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{v.name}</span>
                  <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{v.source}</span>
                </div>
                {v.sub_category && <div className="text-xs text-muted-foreground">{v.sub_category}</div>}
              </div>
              {showAmount && (
                <div className="mr-3">
                  <Input
                    type="number"
                    value={v.amount_invested || ""}
                    onChange={(e) => setAmount(idx, e.target.value)}
                    placeholder="Amount ₹"
                    className="h-8 w-32 text-xs tabular-nums"
                    data-testid={`picked-amount-${idx}`}
                  />
                </div>
              )}
              <Button size="sm" variant="ghost" onClick={() => remove(idx)} data-testid={`picked-remove-${idx}`}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ExternalEntry({ onAdd }) {
  const [name, setName] = useState("");
  const [sub, setSub] = useState("");
  const [large, setLarge] = useState(0);
  const [mid, setMid] = useState(0);
  const [small, setSmall] = useState(0);
  const [r1, setR1] = useState(""); const [r3, setR3] = useState(""); const [r5, setR5] = useState("");

  const submit = () => {
    if (!name) { toast.error("Fund name is required"); return; }
    onAdd({
      source: "external", name, sub_category: sub || undefined,
      market_cap_allocation: { large_pct: Number(large), mid_pct: Number(mid), small_pct: Number(small) },
      trailing_returns: {
        return_1yr_pct: r1 !== "" ? Number(r1) : null,
        return_3yr_pct: r3 !== "" ? Number(r3) : null,
        return_5yr_pct: r5 !== "" ? Number(r5) : null,
      },
    });
    setName(""); setSub(""); setLarge(0); setMid(0); setSmall(0); setR1(""); setR3(""); setR5("");
    toast.success("Fund added");
  };
  return (
    <div className="space-y-3 rounded-md border border-dashed border-border bg-secondary/20 p-3 text-sm">
      <div className="grid gap-3 md:grid-cols-2">
        <div><Label className="text-xs">Fund name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ext-name" /></div>
        <div><Label className="text-xs">Sub-category (optional)</Label><Input value={sub} onChange={(e) => setSub(e.target.value)} data-testid="ext-sub" placeholder="e.g. Flexi Cap" /></div>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <div><Label className="text-xs">Large cap %</Label><Input type="number" value={large} onChange={(e) => setLarge(e.target.value)} data-testid="ext-large" /></div>
        <div><Label className="text-xs">Mid cap %</Label><Input type="number" value={mid} onChange={(e) => setMid(e.target.value)} data-testid="ext-mid" /></div>
        <div><Label className="text-xs">Small cap %</Label><Input type="number" value={small} onChange={(e) => setSmall(e.target.value)} data-testid="ext-small" /></div>
      </div>
      <div className="grid gap-2 md:grid-cols-3">
        <div><Label className="text-xs">1Y return %</Label><Input type="number" value={r1} onChange={(e) => setR1(e.target.value)} data-testid="ext-r1" /></div>
        <div><Label className="text-xs">3Y return %</Label><Input type="number" value={r3} onChange={(e) => setR3(e.target.value)} data-testid="ext-r3" /></div>
        <div><Label className="text-xs">5Y return %</Label><Input type="number" value={r5} onChange={(e) => setR5(e.target.value)} data-testid="ext-r5" /></div>
      </div>
      <Button size="sm" onClick={submit} className="gap-1" data-testid="ext-add-btn"><Plus className="h-3 w-3" /> Add fund</Button>
    </div>
  );
}
